import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { 
  Upload, CheckCircle2, X, Loader2, Building2, 
  User, Mail, Phone, MapPin, FileText, Shield, AlertCircle 
} from 'lucide-react';
import { toast } from 'sonner';

// Create public API instance without auth headers
const publicApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
});

const tradeTypes = [
  'General', 'Plumber', 'Electrician', 'Carpenter', 'Painter', 'Tiler', 
  'Roofer', 'Plasterer', 'Bricklayer', 'Concreter', 'Landscaper',
  'HVAC', 'Glazier', 'Fencer', 'Flooring', 'Cabinet Maker', 'Other'
];

const qldRegions = ['Brisbane', 'Gold Coast', 'Sunshine Coast', 'Wide Bay', 'Ipswich', 'Toowoomba'];

const TradeOnboardPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [branding, setBranding] = useState({});
  const [step, setStep] = useState(1);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company_name: '',
    abn: '',
    address: '',
    trade_types: [],
    regions: [],
    hourly_rate: '',
    notes: '',
    password: '',
    confirmPassword: ''
  });
  
  const [documents, setDocuments] = useState({
    qbcc_licence: null,
    public_liability: null,
    workcover: null,
    professional_indemnity: null
  });

  useEffect(() => {
    fetchInvitation();
  }, [token]);

  const fetchInvitation = async () => {
    try {
      const response = await publicApi.get(`/trade-onboard/${token}`);
      setInvitation(response.data.invitation);
      setBranding(response.data.branding);
      setFormData(prev => ({
        ...prev,
        name: response.data.invitation.name || '',
        email: response.data.invitation.email || '',
        trade_types: response.data.invitation.trade_types || []
      }));
    } catch (err) {
      if (err.response?.status === 410) {
        setError('This invitation has expired or has already been used.');
      } else if (err.response?.status === 404) {
        setError('Invalid invitation link.');
      } else {
        setError('Failed to load invitation. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTradeToggle = (trade) => {
    setFormData(prev => ({
      ...prev,
      trade_types: prev.trade_types.includes(trade)
        ? prev.trade_types.filter(t => t !== trade)
        : [...prev.trade_types, trade]
    }));
  };

  const handleRegionToggle = (region) => {
    setFormData(prev => ({
      ...prev,
      regions: prev.regions.includes(region)
        ? prev.regions.filter(r => r !== region)
        : [...prev.regions, region]
    }));
  };

  const handleDocumentUpload = (docType, file) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        setDocuments(prev => ({ 
          ...prev, 
          [docType]: { file, base64, filename: file.name } 
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validateStep1 = () => {
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return false;
    }
    if (formData.trade_types.length === 0) {
      toast.error('Please select at least one trade type');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!documents.public_liability) {
      toast.error('Public Liability Insurance is required');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.password || formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep3()) return;
    
    setSubmitting(true);
    try {
      const docList = Object.entries(documents)
        .filter(([_, doc]) => doc !== null)
        .map(([docType, doc]) => ({
          document_type: docType,
          file_data: doc.base64,
          filename: doc.filename
        }));

      await publicApi.post(`/trade-onboard/${token}`, {
        ...formData,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        documents: docList
      });

      setSuccess(true);
      toast.success('Registration completed successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Unable to Load Invitation</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Registration Complete!</h2>
            <p className="text-muted-foreground mb-6">
              Welcome to {branding.company_name}! Your account has been created and your documents are being reviewed.
            </p>
            <Button onClick={() => navigate('/trade')} className="w-full">
              Go to Trade Portal
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          {branding.logo_url && (
            <img src={branding.logo_url} alt={branding.company_name} className="h-16 mx-auto mb-4" />
          )}
          <h1 className="text-3xl font-bold">{branding.company_name || 'My Project Agent'}</h1>
          <p className="text-muted-foreground mt-2">Trade Registration</p>
        </div>

        {/* Welcome Message */}
        {invitation?.message && (
          <Card className="mb-6 bg-primary/5 border-primary/20">
            <CardContent className="pt-4">
              <p className="text-sm">
                <span className="font-medium">Message from {invitation.invited_by_name}:</span>
                <br />
                {invitation.message}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                step >= s ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'
              }`}>
                {step > s ? <CheckCircle2 className="h-5 w-5" /> : s}
              </div>
              {s < 3 && <div className={`w-16 h-1 transition-colors ${step > s ? 'bg-primary' : 'bg-secondary'}`} />}
            </div>
          ))}
        </div>
        <div className="flex justify-center gap-8 text-xs text-muted-foreground mb-8">
          <span>Your Details</span>
          <span>Documents</span>
          <span>Create Account</span>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              {step === 1 && 'Your Details'}
              {step === 2 && 'Upload Documents'}
              {step === 3 && 'Create Your Account'}
            </CardTitle>
            <CardDescription>
              {step === 1 && 'Tell us about yourself and your business'}
              {step === 2 && 'Upload your certifications and insurance documents'}
              {step === 3 && 'Set up your login credentials for the Trade Portal'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Step 1: Details */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2"><User className="h-4 w-4" /> Full Name *</Label>
                    <Input 
                      value={formData.name} 
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="John Smith"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2"><Building2 className="h-4 w-4" /> Company Name</Label>
                    <Input 
                      value={formData.company_name} 
                      onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                      placeholder="Smith Plumbing Pty Ltd"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2"><Mail className="h-4 w-4" /> Email *</Label>
                    <Input 
                      type="email"
                      value={formData.email} 
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="john@smithplumbing.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2"><Phone className="h-4 w-4" /> Phone *</Label>
                    <Input 
                      value={formData.phone} 
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="0400 000 000"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>ABN</Label>
                    <Input 
                      value={formData.abn} 
                      onChange={(e) => setFormData({...formData, abn: e.target.value})}
                      placeholder="12 345 678 901"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Hourly Rate ($)</Label>
                    <Input 
                      type="number"
                      value={formData.hourly_rate} 
                      onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                      placeholder="85"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><MapPin className="h-4 w-4" /> Address</Label>
                  <Input 
                    value={formData.address} 
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="123 Trade Street, Brisbane QLD 4000"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Trade Types *</Label>
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-2 p-3 bg-secondary/50 rounded-lg border">
                    {tradeTypes.map(trade => (
                      <div key={trade} className="flex items-center space-x-2">
                        <Checkbox 
                          id={`trade-${trade}`} 
                          checked={formData.trade_types.includes(trade)} 
                          onCheckedChange={() => handleTradeToggle(trade)} 
                        />
                        <label htmlFor={`trade-${trade}`} className="text-sm cursor-pointer">{trade}</label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Service Regions</Label>
                  <div className="flex flex-wrap gap-3 p-3 bg-secondary/50 rounded-lg border">
                    {qldRegions.map(region => (
                      <div key={region} className="flex items-center space-x-2">
                        <Checkbox 
                          id={`region-${region}`} 
                          checked={formData.regions.includes(region)} 
                          onCheckedChange={() => handleRegionToggle(region)} 
                        />
                        <label htmlFor={`region-${region}`} className="text-sm cursor-pointer">{region}</label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button onClick={() => validateStep1() && setStep(2)}>
                    Next: Upload Documents
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Documents */}
            {step === 2 && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Upload clear images or PDFs of your documents. These will be reviewed by the team.
                </p>

                {[
                  { key: 'qbcc_licence', label: 'QBCC Licence', required: false, icon: FileText },
                  { key: 'public_liability', label: 'Public Liability Insurance', required: true, icon: Shield },
                  { key: 'workcover', label: 'WorkCover Certificate', required: false, icon: Shield },
                  { key: 'professional_indemnity', label: 'Professional Indemnity', required: false, icon: Shield },
                ].map(({ key, label, required, icon: Icon }) => (
                  <div key={key} className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg border">
                    <div className="flex items-center gap-3">
                      {documents[key] ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : (
                        <Icon className="h-5 w-5 text-muted-foreground" />
                      )}
                      <div>
                        <p className="font-medium">
                          {label} {required && <span className="text-destructive">*</span>}
                        </p>
                        {documents[key] && (
                          <p className="text-xs text-muted-foreground">{documents[key].filename}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {documents[key] && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setDocuments(prev => ({...prev, [key]: null}))}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                      <label className="cursor-pointer">
                        <Input 
                          type="file" 
                          className="hidden" 
                          accept="image/*,.pdf" 
                          onChange={(e) => handleDocumentUpload(key, e.target.files[0])} 
                        />
                        <span className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90">
                          <Upload className="h-4 w-4" />
                          {documents[key] ? 'Replace' : 'Upload'}
                        </span>
                      </label>
                    </div>
                  </div>
                ))}

                <div className="space-y-2">
                  <Label>Additional Notes</Label>
                  <Textarea 
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Any additional information about your qualifications, experience, or availability..."
                    rows={3}
                  />
                </div>

                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                  <Button onClick={() => validateStep2() && setStep(3)}>
                    Next: Create Account
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Account Creation */}
            {step === 3 && (
              <div className="space-y-6">
                <p className="text-sm text-muted-foreground">
                  Create a password to access the Trade Portal where you can view your jobs, purchase orders, and schedule.
                </p>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Email (your login)</Label>
                    <Input value={formData.email} disabled className="bg-secondary/50" />
                  </div>

                  <div className="space-y-2">
                    <Label>Password *</Label>
                    <Input 
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="Minimum 8 characters"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Confirm Password *</Label>
                    <Input 
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                      placeholder="Re-enter your password"
                    />
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-secondary/30 rounded-lg p-4">
                  <h4 className="font-medium mb-3">Registration Summary</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-muted-foreground">Name:</span>
                    <span>{formData.name}</span>
                    <span className="text-muted-foreground">Email:</span>
                    <span>{formData.email}</span>
                    <span className="text-muted-foreground">Phone:</span>
                    <span>{formData.phone}</span>
                    <span className="text-muted-foreground">Trades:</span>
                    <span>{formData.trade_types.join(', ')}</span>
                    <span className="text-muted-foreground">Documents:</span>
                    <span>{Object.values(documents).filter(d => d !== null).length} uploaded</span>
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                  <Button onClick={handleSubmit} disabled={submitting}>
                    {submitting ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</>
                    ) : (
                      'Complete Registration'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-8">
          Having trouble? Contact {branding.email || 'support'} or call {branding.phone || 'us'}
        </p>
      </div>
    </div>
  );
};

export default TradeOnboardPage;
